import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useSelector } from "react-redux";
import { type CoAdminRuleType } from "@/lib/travelHealth/coAdmin/coAdminRules";
import { findRuleType } from "@/lib/travelHealth/coAdmin/findRuleType";
import type {
  DoseScheduleEntry,
  TravelHealthDoseAuditRecord,
  TravelHealthPlanProduct,
  TravelHealthProductStatus,
  TripType,
  TravelActivity,
} from "@/lib/travelHealth/types/travelHealth.types";

// ─────────────────────────────────────────────────────────────────────────────
// PROPS
// ─────────────────────────────────────────────────────────────────────────────

interface TravelHealthPlanTimelineProps {
  planId: string;
  onOpenTier2: (productId: string, pgdId: string) => void;
  onClose: () => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// DISPLAY CONSTANTS — presentation only, no clinical logic
// ─────────────────────────────────────────────────────────────────────────────

const TRIP_TYPE_LABELS: Record<TripType, string> = {
  holiday: "Holiday",
  "business-trip": "Business trip",
  backpacking: "Backpacking",
  "hotel-stay": "Hotel stay",
  cruise: "Cruise",
  "camping-hostels": "Camping / hostels",
  "living-abroad": "Living abroad (expatriate)",
  safari: "Safari",
  "adventure-travel": "Adventure travel",
  "volunteer-relief-work": "Volunteer or relief work",
  "hajj-umrah": "Pilgrimage (Hajj / Umrah)",
  diving: "Diving",
  "healthcare-worker": "Healthcare worker",
  "medical-dental-treatment-abroad": "Medical or dental treatment abroad",
  "visiting-friends-relatives": "Visiting friends and relatives",
};

const ACTIVITY_LABELS: Record<TravelActivity, string> = {
  "trekking-high-altitude": "Trekking or time at high altitude (over 2,500 m)",
  "freshwater-contact":
    "Swimming, rafting or contact with fresh water (lakes/rivers)",
  "animal-contact": "Contact with animals, including dogs, monkeys or bats",
  "outdoor-remote-areas":
    "Cycling, running or being outdoors in remote areas",
  "camping-sleeping-outdoors": "Camping or sleeping outdoors",
  "outside-dawn-dusk-overnight": "Being outside at dawn, dusk or overnight",
  "healthcare-settings": "Working in or visiting healthcare settings",
  "relief-aid-work": "Relief or aid work",
  "farming-animals": "Farming or working with animals",
  "caves-bat-exposure": "Caves or possible bat exposure",
  "mass-gathering":
    "A mass gathering (e.g. Hajj/Umrah, festival, sporting event)",
  "rural-remote-far-from-care":
    "Rural or remote areas, far from medical care",
  "long-stay-over-one-month": "A long stay — more than one month",
  "adventure-extreme-sports": "Adventure or extreme sports",
  "tattoos-piercings-cosmetic-abroad":
    "Tattoos, piercings or cosmetic procedures abroad",
  "new-sexual-partner": "A new sexual partner while travelling",
  "long-haul-5plus-timezones":
    "A long-haul flight crossing 5 or more time zones",
};

const STATUS_LABELS: Record<TravelHealthProductStatus, string> = {
  planned: "Planned",
  "in-progress": "In progress",
  complete: "Complete",
  declined: "Declined",
  referred: "Referred",
};

const STATUS_BADGE_CLASSES: Record<TravelHealthProductStatus, string> = {
  planned: "bg-gray-100 text-gray-700 border-gray-300",
  "in-progress": "bg-blue-100 text-blue-700 border-blue-300",
  complete: "bg-green-100 text-green-700 border-green-300",
  declined: "bg-red-100 text-red-700 border-red-300",
  referred: "bg-amber-100 text-amber-700 border-amber-300",
};

const RULE_TYPE_LABELS: Record<CoAdminRuleType, string> = {
  timing: "Timing",
  advisory: "Advisory",
  "product-conditional": "Product-conditional",
};

const PLAN_STATUS_BADGE_CLASSES: Record<string, string> = {
  active: "bg-blue-100 text-blue-700 border-blue-300",
  complete: "bg-green-100 text-green-700 border-green-300",
  abandoned: "bg-gray-100 text-gray-500 border-gray-300",
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS — display formatting only
// ─────────────────────────────────────────────────────────────────────────────

function formatDateDDMMYYYY(dateIso: string): string {
  const [year, month, day] = dateIso.split("-");
  if (!year || !month || !day) return dateIso;
  return `${day}/${month}/${year}`;
}

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysBetween(fromIso: string, toIso: string): number | null {
  const from = new Date(`${fromIso}T00:00:00`);
  const to = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
}


// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

export function TravelHealthPlanTimeline({
  planId,
  onOpenTier2,
  onClose,
}: TravelHealthPlanTimelineProps) {
  const plan = useSelector((state: any) =>
    state.travelHealth.plans.find((p: any) => p.id === planId),
  );
  const visits = useSelector((state: any) =>
    state.travelHealth.visits.filter((visit: any) => visit.planId === planId),
  );
  const doseAuditRecords = useSelector(
    (state: any) => state.travelHealth.doseAuditRecords,
  );

  const [coAdminExpanded, setCoAdminExpanded] = useState(false);
  const [selectedDoseForDetail, setSelectedDoseForDetail] = useState<{
    product: TravelHealthPlanProduct;
    dose: DoseScheduleEntry;
  } | null>(null);

  if (!plan) {
    return (
      <div className="p-6 space-y-4">
        <p className="text-sm text-gray-700">Plan not found.</p>
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
      </div>
    );
  }

  const todayIso = todayIsoDate();
  const tripCompleted = plan.returnDate <= todayIso;

  const departureDaysDiff = daysBetween(todayIso, plan.departureDate);

  return (
    <div className="p-6 space-y-6">
      {/* ── SECTION 1: Plan header ─────────────────────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            Travel Health Plan
          </h2>
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={PLAN_STATUS_BADGE_CLASSES[plan.status]}
            >
              {plan.status}
            </Badge>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className="border rounded-lg p-4 space-y-2">
          <p className="text-sm text-gray-700">
            <span className="font-medium">Destinations: </span>
            {plan.tripDestinations.join(", ")}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Trip types: </span>
            {plan.tripTypes.map((tripType) => TRIP_TYPE_LABELS[tripType]).join(", ")}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Activities: </span>
            {plan.activities.map((activity) => ACTIVITY_LABELS[activity]).join(", ")}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Departure: </span>
            {formatDateDDMMYYYY(plan.departureDate)}
          </p>
          <p className="text-sm text-gray-700">
            <span className="font-medium">Return: </span>
            {formatDateDDMMYYYY(plan.returnDate)}
          </p>
          {departureDaysDiff !== null && (
            <p className="text-sm text-gray-700">
              {departureDaysDiff >= 0
                ? `Days until departure: ${departureDaysDiff}`
                : `Departed ${Math.abs(departureDaysDiff)} days ago`}
            </p>
          )}
          {plan.nathnacConsulted && (
            <p className="text-sm text-green-700">
              NaTHNaC consulted {"\u2713"}
            </p>
          )}
          {plan.nathnacConsulted && (
            <div className="space-y-1">
              <p className="text-sm font-medium text-gray-900">
                NaTHNaC assessment
              </p>
              {plan.nathnacDestinationsAssessed && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Destinations assessed:</span>{" "}
                  {plan.nathnacDestinationsAssessed}
                </p>
              )}
              {plan.nathnacVaccinesRecommended && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">
                    Vaccines recommended/required:
                  </span>{" "}
                  {plan.nathnacVaccinesRecommended}
                </p>
              )}
              {plan.nathnacMalariaRiskResistanceAgent && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">
                    Malaria risk/resistance/agent:
                  </span>{" "}
                  {plan.nathnacMalariaRiskResistanceAgent}
                </p>
              )}
              {plan.nathnacEntryRequirements && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">Entry requirements:</span>{" "}
                  {plan.nathnacEntryRequirements}
                </p>
              )}
              {plan.nathnacOtherMedicinesIndicated && (
                <p className="text-sm text-gray-700">
                  <span className="font-medium">
                    Other medicines indicated:
                  </span>{" "}
                  {plan.nathnacOtherMedicinesIndicated}
                </p>
              )}
            </div>
          )}
          {plan.menACWYEntitlementAcknowledged === true && (
            <p className="text-sm text-green-700">
              MenACWY NHS entitlement advisory given {"\u2713"}
            </p>
          )}
          {plan.dengueEvidenceDescription && (
            <p className="text-sm text-gray-700">
              <span className="font-medium">Dengue evidence reviewed:</span>{" "}
              {plan.dengueEvidenceDescription}
            </p>
          )}
        </div>
      </div>

      {/* ── SECTION 2: Co-administration constraints panel ────────────── */}
      {plan.coAdminMatrixOutput.length > 0 && (
        <Collapsible open={coAdminExpanded} onOpenChange={setCoAdminExpanded}>
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full text-left border rounded-lg px-4 py-3 text-sm font-medium text-gray-900 bg-gray-50 hover:bg-gray-100"
            >
              {`Co-administration constraints (${plan.coAdminMatrixOutput.length}) — tap to ${
                coAdminExpanded ? "hide" : "review"
              }`}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            {plan.coAdminMatrixOutput.map((constraint, index) => {
              const ruleType = findRuleType(constraint);
              return (
                <div
                  key={`${constraint.pgdIdA}-${constraint.pgdIdB}-${index}`}
                  className="border rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-medium text-gray-900">
                      {constraint.productNameA} / {constraint.productNameB}
                    </p>
                    {ruleType && (
                      <Badge variant="outline">
                        {RULE_TYPE_LABELS[ruleType]}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-700">{constraint.rule}</p>
                  {constraint.schedulingRecommendation && (
                    <div className="ml-3 pl-3 border-l-2 border-amber-300 bg-amber-50 rounded-r-md py-2 pr-2">
                      <p className="text-xs font-medium text-gray-500">
                        Scheduling recommendation
                      </p>
                      <p className="text-sm text-gray-700">
                        {constraint.schedulingRecommendation}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* ── SECTION 3: Timeline ────────────────────────────────────────── */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h3 className="font-medium text-gray-900">Timeline</h3>
          {tripCompleted && (
            <span className="text-xs font-medium text-gray-500">
              Trip completed
            </span>
          )}
        </div>

        <div className="space-y-3">
          {plan.basket.map((product) => (
            <ProductDoseList
              key={product.id}
              product={product}
              onOpenTier2={onOpenTier2}
              onSelectDose={(dose) => setSelectedDoseForDetail({ product, dose })}
            />
          ))}
        </div>
      </div>

      {/* ── SECTION 4: Visit history ───────────────────────────────────── */}
      {visits.length > 0 && (
        <div className="space-y-3">
          <h3 className="font-medium text-gray-900">Visit history</h3>
          <div className="space-y-2">
            {[...visits]
              .sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0))
              .map((visit) => {
                const productNames = visit.productsActioned
                  .map(
                    (productId) =>
                      plan.basket.find((product) => product.id === productId)
                        ?.productName,
                  )
                  .filter((name): name is string => Boolean(name));
                return (
                  <div key={visit.id} className="border rounded-lg p-3 space-y-1">
                    <p className="text-sm font-medium text-gray-900">
                      {formatDateDDMMYYYY(visit.date)}
                    </p>
                    <p className="text-sm text-gray-700">
                      Pharmacist: {visit.pharmacistName}
                    </p>
                    <p className="text-sm text-gray-700">
                      Products actioned:{" "}
                      {productNames.length > 0 ? productNames.join(", ") : "—"}
                    </p>
                    <p className="text-xs text-gray-500">
                      {visit.auditRecordIds.length} audit record
                      {visit.auditRecordIds.length === 1 ? "" : "s"}
                    </p>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      <Dialog
        open={selectedDoseForDetail !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedDoseForDetail(null);
        }}
      >
        <DialogContent>
          {selectedDoseForDetail && (
            <DoseDetailDialogContent
              product={selectedDoseForDetail.product}
              dose={selectedDoseForDetail.dose}
              auditRecord={doseAuditRecords.find(
                (record) => record.auditRecordId === selectedDoseForDetail.dose.auditRecordId,
              )}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PRODUCT DOSE LIST — display only
// Product name, status badge and action button, followed by a plain list
// of doses. Clicking a dose row opens the detail dialog.
// ─────────────────────────────────────────────────────────────────────────────

interface ProductDoseListProps {
  product: TravelHealthPlanProduct;
  onOpenTier2: (productId: string, pgdId: string) => void;
  onSelectDose: (dose: DoseScheduleEntry) => void;
}

function ProductDoseList({
  product,
  onOpenTier2,
  onSelectDose,
}: ProductDoseListProps) {
  const renderActionCell = () => {
    switch (product.status) {
      case "planned":
        return (
          <Button
            size="sm"
            onClick={() => onOpenTier2(product.id, product.pgdId)}
            className="bg-slate-900 hover:bg-slate-800"
          >
            Begin pathway
          </Button>
        );
      case "in-progress":
        return (
          <Button
            size="sm"
            onClick={() => onOpenTier2(product.id, product.pgdId)}
            className="bg-slate-900 hover:bg-slate-800"
          >
            {`Continue — dose ${product.currentDoseNumber + 1}`}
          </Button>
        );
      case "complete":
        return (
          <span className="text-sm text-green-700">
            Complete {"\u2713"}
          </span>
        );
      case "declined":
        return <span className="text-sm text-red-700">Declined</span>;
      case "referred":
        return <span className="text-sm text-amber-700">Referred to GP</span>;
      default:
        return null;
    }
  };

  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <p className="text-sm font-medium text-gray-900">
            {product.productName}
            {product.blackTriangle ? " \u25BC" : ""}
          </p>
          <Badge
            variant="outline"
            className={STATUS_BADGE_CLASSES[product.status]}
          >
            {STATUS_LABELS[product.status]}
          </Badge>
        </div>
        {renderActionCell()}
      </div>

      {product.doseSchedule.length > 0 && (
        <ul className="divide-y">
          {product.doseSchedule.map((dose) => (
            <li
              key={dose.doseNumber}
              className="py-2 px-1 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              onClick={() => onSelectDose(dose)}
            >
              <span className="text-sm text-gray-900">
                Dose {dose.doseNumber}
              </span>
              <span className="text-sm text-gray-600">
                {dose.administeredDate
                  ? `Given ${formatDateDDMMYYYY(dose.administeredDate)}`
                  : `Due ${formatDateDDMMYYYY(dose.scheduledDate)}`}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DOSE DETAIL DIALOG CONTENT — display only
// ─────────────────────────────────────────────────────────────────────────────

interface DoseDetailDialogContentProps {
  product: TravelHealthPlanProduct;
  dose: DoseScheduleEntry;
  auditRecord: TravelHealthDoseAuditRecord | undefined;
}

function DoseDetailDialogContent({
  product,
  dose,
  auditRecord,
}: DoseDetailDialogContentProps) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {product.productName} — Dose {dose.doseNumber}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-3">
        <p className="text-sm text-gray-700">
          {dose.administeredDate
            ? `Date given: ${formatDateDDMMYYYY(dose.administeredDate)}`
            : `Date due: ${formatDateDDMMYYYY(dose.scheduledDate)}`}
        </p>

        {auditRecord ? (
          <div className="border rounded-lg p-3 space-y-1">
            <p className="text-sm text-gray-700">
              <span className="font-medium">Brand: </span>
              {auditRecord.brandName}
            </p>
            {auditRecord.route && (
              <p className="text-sm text-gray-700">
                <span className="font-medium">Route: </span>
                {auditRecord.route}
              </p>
            )}
            <p className="text-sm text-gray-700">
              <span className="font-medium">Batch number: </span>
              {auditRecord.batchNumber}
            </p>
            <p className="text-sm text-gray-700">
              <span className="font-medium">Expiry date: </span>
              {auditRecord.expiryDate}
            </p>
            {auditRecord.injectionSite && (
              <p className="text-sm text-gray-700">
                <span className="font-medium">Injection site: </span>
                {auditRecord.injectionSite}
              </p>
            )}
            <p className="text-sm text-gray-700">
              <span className="font-medium">Adverse reaction: </span>
              {auditRecord.adverseReaction.occurred
                ? auditRecord.adverseReaction.details || "Reported"
                : "None reported"}
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            This dose has not been recorded yet.
          </p>
        )}
      </div>
    </>
  );
}

export type { TravelHealthPlanTimelineProps };
